<template>
  <div class="Booking">
    <div
      v-if="working"
      class="working">
      <LoadingIcon />
    </div>
    <div class="panel">
      <div class="membership">
        <div class="member">
          <div class="gender">
            <GenderAge
              :birthdate="booking.membership.customer.birthdate"
              :gender="booking.membership.customer.gender" />
          </div>

          <div class="customer">
            <div class="created">
              {{ createdAt }}
              - {{ booking.location.name }}
            </div>

            <div class="membership-status-wrapper">
              <div
                class="badge membership-status"
                :class="booking.membership.status">
                {{ status }}
              </div>
            </div>

            <div class="contactinfo">
              <div class="fullname">
                {{ booking.membership.customer.firstName }}
                {{ booking.membership.customer.lastName }}
              </div>
              <div class="mobile">
                {{ booking.membership.customer.mobilePrefix }}
                {{ booking.membership.customer.mobileNumber }}
                <a :href="'mailto:'+booking.membership.customer.username">
                  {{ booking.membership.customer.username }}
                </a>
              </div>
            </div>


            <div
              class="badge"
              :class="booking.type">
              <span v-if="booking.type === 'PromotionPersonalTrainerBooking' && booking.membership.promotion">
                {{ booking.membership.promotion.name }}
              </span>

              <span v-else-if="booking.packageOrder">
                {{ this.booking.packageOrder.package.title }}
              </span>

              <span v-else-if="booking.type === 'MembershipPersonalTrainerBooking'">
                PT Booking via My Page
              </span>

              <span v-else>
                {{ booking.type }}
              </span>
            </div>

            <div
              class="messages-toggle"
              @click="showMessages = !showMessages">
              {{ booking.messagesList.length }} comments
              <Icon
                name="arrow"
                :direction="showMessages ? 'down' : 'up'" />
            </div>
          </div>
        </div>

        <div class="assign">
          <div class="mobile-menu">
            <div @click="showMobileMenu = !showMobileMenu">
              <Icon name="overflow" />
            </div>

            <DropdownMenu
              v-if="showMobileMenu"
              @close="showMobileMenu = false">
              <li
                v-if="canAssign"
                @click="assign()">
                <Icon name="calendar" />
                <span>Assign to me</span>
              </li>

              <li @click="showComment = true">
                <Icon name="bubble" />
                <span>Add comment</span>
              </li>

              <li
                class="strong"
                @click="showSMS = true">
                <Icon
                  name="send"
                  icon-color="var(--blue-purple)" />
                <span>Send SMS</span>
              </li>

              <li
                v-if="canUnassign"
                @click="unassign()">
                <Icon name="refund" />
                <span>Move to new</span>
              </li>

              <li
                v-if="canCancel"
                @click="cancel()">
                <Icon name="trash" />
                <span>Cancel</span>
              </li>

              <li
                v-if="canArchive"
                @click="archive()">
                <Icon name="save" />
                <span>Archive</span>
              </li>

              <li
                class="strong sep"
                v-if="canDeliver"
                @click="delivered()">
                <Icon
                  name="success"
                  icon-color="var(--dark-seafoam" />
                <span>Mark as done</span>
              </li>
            </DropdownMenu>
          </div>

          <div class="buttons">
            <IconButton
              icon="calendar"
              v-if="canAssign"
              @click="assign()"
              class="green">
              Assign {{ booking.membership.customer.firstName }} to me
            </IconButton>

            <IconButton
              icon="refund"
              v-if="canUnassign"
              @click="unassign()"
              class="cta2">
              Move back to new
            </IconButton>

            <IconButton
              icon="success"
              v-if="canDeliver"
              @click="delivered()"
              class="cta2 delivered">
              Mark as delivered
            </IconButton>
          </div>
        </div>
      </div>
      <!-- membership -->

      <div
        class="messages"
        :class="{ show: showMessages }">
        <Comment
          class="message"
          v-for="message of booking.messagesList"
          :message="message"
          :key="message.id" />
        <SMS
          class="message"
          v-for="message of booking.membership.smsByReceiverList"
          :message="message"
          :key="message.id" />
        <!-- message -->
      </div>
      <!-- messages -->
      <div class="actions">
        <form
          ref="msgForm"
          @submit.prevent="addComment()">
          <div class="input">
            <input
              type="text"
              v-model="msg"
              ref="msgInput"
              placeholder="Write a comment"
              required>
            <button class="btn small">
              <Icon name="send" />
            </button>
          </div>
          <div
            v-if="commentError"
            class="error">
            {{ commentError }}
          </div>
          <IconButton
            icon="send"
            class="cta2 cms small"
            @click="showSMS = true">
            Send SMS
          </IconButton>
        </form>

        <div
          class="cancel"
          v-if="canCancel"
          @click="cancel()">
          Cancel
          <Icon name="close" />
        </div>

        <div
          class="cancel"
          v-if="canArchive"
          @click="archive()">
          <span>Archive booking</span>
          <Icon name="save" />
        </div>
      </div>
      <!-- actions -->
      <Modal
        v-if="showSMS"
        @close="showSMS = false">
        <h3 slot="header">
          Send SMS
        </h3>
        <div slot="body">
          <form
            @submit.prevent="sendSMS()"
            ref="smsForm">
            <label>To</label>
            {{ booking.membership.customer.mobilePrefix }}
            {{ booking.membership.customer.mobileNumber }}
            <div class="form-group">
              <label for="id-sms">Message</label>
              <textarea
                id="sms"
                ref="smsInput"
                v-model="sms"
                cols="90"
                rows="8"
                placeholder="Your message..."
                required />
            </div>
          </form>
        </div>
        <div slot="footer">
          <form @submit.prevent="sendSMS()">
            <button
              type="submit"
              class="btn small">
              <Icon name="send" />
            </button>
            <button
              @click.prevent="showSMS = false"
              class="btn small cta2">
              Cancel
            </button>
          </form>
        </div>
      </Modal>

      <Modal
        v-if="showComment"
        @close="showComment = false">
        <h3 slot="header">
          Add comment
        </h3>
        <div slot="body">
          <form
            @submit.prevent="addComment()"
            ref="commentForm">
            <div class="form-group">
              <label for="id-comment">Comment</label>
              <textarea
                id="id-comment"
                v-model="msg"
                placeholder="Write a comment"
                required />
            </div>
          </form>
        </div>
        <div slot="footer">
          <form @submit.prevent="addComment()">
            <button
              type="submit"
              class="btn small">
              <Icon name="send" />
            </button>
            <button
              @click.prevent="showComment = false"
              class="btn small cta2">
              Cancel
            </button>
          </form>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import moment from "moment";
import Modal from "@/components/Modal";
import Icon from "@/icons/Icon";
import LoadingIcon from "@/icons/LoadingIcon";
import DropdownMenu from "@/components/DropdownMenu";
import GenderAge from "@/components/GenderAge";
import SMS from "./SMS";
import Comment from "./Comment";

export default {
  components: { Modal, Icon, DropdownMenu, GenderAge, LoadingIcon, SMS, Comment, },
  props: {
    booking: {
      type: Object,
      required: true,
    },
  },

  data: function () {
    return {
      msg: "",
      sms: "",
      error: "",
      commentError: null,
      showSMS: false,
      showComment: false,
      smsError: null,
      showMobileMenu: false,
      showMessages: false,
      working: false,
    };
  },

  filters: {
    dateformat: (value, format) => {
      return moment(value).format(format);
    },
  },

  computed: {
    status() {
      return this.booking.membership.status.replace('_', ' ');
    },
    ptId() {
      return this.$store.getters["profile"].id;
    },

    canDeliver() {
      return this.booking.status === "ASSIGNED";
    },

    canAssign() {
      return (this.booking.status !== "ASSIGNED");
    },

    canCancel() {
      return ["ASSIGNED", "DELIVERED"].indexOf(this.booking.status) > -1;
    },

    canUnassign() {
      return ["ASSIGNED", "CANCELLED", "DELIVERED"].indexOf(this.booking.status) > -1;
    },

    canArchive() {
      return ["NEW"].indexOf(this.booking.status) > -1;
    },

    createdAt() {
      return moment(this.booking.createdAt).format("dddd D MMM YYYY HH:mm");
    },
  },

  methods: {
    assign() {
      console.info("assigning booking", this.booking.id);
      this.working = true;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation BookingAssign($bookingId: UUID!) {
              bookingAssign(input: { _bookingId: $bookingId }) {
                results {
                  id
                  ptId
                  status
                }
              }
            }
          `,
          variables: { bookingId: this.booking.id },
        })
        .then((result) => {
          console.log(result);
          console.log("emitting...");
          this.$emit("bookings-updated", result.data.bookingAssign.results);
        })
        .catch((err) => {
          console.error("error assigning booking", err);
        })
        .finally(() => (this.working = false));
    },

    updateBookingStatus(status, ptId) {
      this.working = true;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation UpdateBooking(
              $bookingId: UUID!
              $status: String!
              $ptId: UUID
            ) {
              updateBooking(
                input: {
                  patch: { status: $status, ptId: $ptId }
                  id: $bookingId
                }
              ) {
                booking {
                  ptId
                  status
                  id
                }
              }
            }
          `,
          variables: { bookingId: this.booking.id, status, ptId },
        })
        .then((result) => {
          this.$emit("bookings-updated", [result.data.updateBooking.booking]);
        })
        .catch((err) => {
          console.error("error updating booking", err);
        })
        .finally(() => {
          this.working = false;
        });
    },

    archive() {
      this.updateBookingStatus("ARCHIVED", null);
    },

    unassign() {
      console.log("unassign", this.booking.id);
      this.updateBookingStatus("NEW", null);
    },

    delivered() {
      console.log("delivered", this.booking.id);
      this.updateBookingStatus("DELIVERED", this.ptId);
    },

    cancel() {
      console.log("cancel", this.booking.id);
      this.updateBookingStatus("CANCELLED", this.ptId);
    },

    sendSMS() {
      this.smsError = null;
      const sms = this.sms;
      this.sms = "";
      this.createSMS(sms)
        .then((result) => {
          this.booking.membership.smsByReceiverList.push(result.data.createSm.sm);
          this.$refs.smsForm.reset();
          this.$refs.smsInput.blur();
          this.showSMS = false;
        })
        .catch((err) => {
          console.error(err);
          this.smsError = err;
          this.sms = sms;
          this.$refs.smsInput.focus();
        });
    },

    createSMS(msg) {
      return this.$apollo.mutate({
        mutation: gql`
        mutation CreateSMS($msg: String!, $receiver: String!) {
          __typename
          createSm(input: { sm: { msg: $msg, receiver: $receiver } }) {
            clientMutationId
            sm {
              createdAt
              id
              msg
              status
            }
          }
        }`,
        variables: {
          msg,
          receiver: this.booking.membership.id,
        },
      });
    },

    addComment() {
      this.commentError = null;
      const msg = this.msg;
      this.msg = "";
      this.createMessage(msg)
        .then((result) => {
          this.showComment = false;
          this.booking.messagesList.push(result.data.createMessage.message);
          this.$refs.msgForm.reset();
          this.$refs.msgInput.blur();
        })
        .catch((err) => {
          console.error(err);
          this.commentError = err;
          this.msg = msg;
          this.$refs.msgInput.focus();
        });
    },

    createMessage(msg) {
      return this.$apollo.mutate({
        mutation: gql`
          mutation CreateMessage(
            $bookingId: UUID!
            $msg: String!
          ) {
            createMessage(
              input: {
                message: { bookingId: $bookingId, msg: $msg }
              }
            ) {
              message {
                id
                msg
                createdAt
                sender
                fromList {
                  firstName
                  lastName
                }
              }
            }
          }
        `,
        variables: {
          bookingId: this.booking.id,
          msg,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/responsive";

.Booking {
  display: flex;
  padding: 1em;

  .working {
    position: absolute;
    width: 100%;
    height: 100;
  }

  .mobile-menu {
    display: none;
  }

  .btn {
    white-space: nowrap;

    &.sms {
      display: flex;
      align-items: baseline;
    }

    &.delivered {
      color: var(--dark-seafoam);
      border-color: var(--dark-seafoam);
    }
  }

  .panel {
    flex-direction: column;
    display: flex;
    flex: 1;
  }

  .member {
    display: flex;
  }

  .membership {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .line {
    padding-top: 1em;
    padding-bottom: 1em;
    width: 100%;
  }

  .gender {
    padding: 1em;
    width: 5em;
  }

  .customer {
    display: flex;
    flex-direction: column;
    padding-bottom: 1em;
  }

  .contactinfo {
    display: flex;
    align-items: baseline;
  }

  .fullname {
    font-size: 1.428em;
    font-weight: bold;
    flex-grow: auto;
  }

  .mobile {
    font-size: 1em;
    font-weight: normal;
    color: var(--midnight-blue);
    opacity: 0.9;
  }

  .badge {
    text-align: center;
    border-radius: 100px;
    color: white;
    font-size: 0.857em;
    font-weight: bold;
    padding: 0.5em;
    padding-left: 1em;
    padding-right: 1em;
    background-color: var(--blue-purple);
    margin-right: 2px;
  }

  .membership-status-wrapper {
    display: flex;
  }

  .membership-status {
    text-transform: capitalize;
    background-color: var(--bright-orange);

    &.active {
      background-color: var(--dark-seafoam);
    }
    &.in_trial {
      background-color: var(--heather);
    }
    &.presale {
      background-color: var(--heather);
    }
    &.in_cancellation {
      background-color: var(--bright-orange);
    }
    &.cancelled_in_presale {
      background-color: var(--red-pink);
    }
    &.cancelled {
      background-color: var(--red-pink);
    }
    &.stopped {
      background-color: var(--red-pink);
    }
  }

  .FollowUpPersonalTrainerBooking {
    background-color: var(--pink);
  }

  .PromotionPersonalTrainerBooking {
    background-color: var(--blue-purple);
  }

  .MembershipPersonalTrainerBooking {
     background-color: var(--dark-seafoam);
  }

  .RecruitmentRewardPersonalTrainerBooking {
    background-color: var(--blue-purple);
  }


  .messages {
    border-top: solid 2px rgba(151, 151, 151, 0.37);
    .message {
      padding: 1em;
      margin-top: 1em;
    }
  }
  .cancel {
    cursor: pointer;
    flex: 1;
    align-self: end;
    text-align: right;
  }

  .actions {
    display: flex;
    padding-top: 1em;

    form {
      display: flex;

      .input {
        display: flex;
        align-items: center;
        padding-right: 1em;
      }
      input {
        padding: 0.8em;
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
      }

      .btn {
        padding: 0.9em;
      }
    }

    .cancel {
      cursor: pointer;
      font-weight: bold;
      opacity: 0.5;
      color: var(--midnight-blue);
      align-self: end;
    }
  }

  .location {
    text-align: center;
  }

  .assign {
    display: flex;
    flex-direction: column;
    align-self: baseline;
    .buttons {
      display: flex;
      .IconButton {
        margin-left: 5px;
      }
    }
    button {
      margin-left: 1em;
    }
  }

  .messages-toggle {
    display: none;
    color: var(--blue-purple);
    font-weight: bold;
    padding-top: 0.5em;
    cursor: pointer;
  }

  @include phone {
    .mobile-menu {
      display: inherit;
      cursor: pointer;
    }

    .contactinfo {
      flex-direction: column;
      .fullname {
        font-size: 1.142em;
      }

      .mobile {
        font-size: 0.857em;
      }
    }

    .gender,
    .customer {
      padding: 0;
      padding-right: 1em;
    }

    .messages-toggle {
      display: inherit;
    }

    .messages {
      display: none;
      border-top: none;

      &.show {
        display: flex;
        flex-direction: column;
      }
    }

    .actions {
      display: none;
    }

    .assign {
      .buttons {
        display: none;
      }
      .location {
        display: none;
      }
    }
  }
}
</style>
